import { zodResolver } from '@hookform/resolvers/zod'
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { useBlocker, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog'
import { formatNumberToCurrency } from '@/helper/currency'
import { Cities } from '@/models/cities'
import { PerimeterCoordinatesResponse } from '@/models/perimeter-coordinates'
import { PropertyDocuments } from '@/models/property-documents'
import {
  PropertyFormResponse,
  UploadPerimeterCoordinates,
} from '@/models/property-form'
import { PropertyImages } from '@/models/property-images'
import {
  ContractSignaturesSchema,
  propertyFormValidationSchema,
  PropertyFormValues,
} from '@/schemas/property-schema'
import api from '@/services/api'

interface PropertyFormContextProps {
  formProperty: UseFormReturn<PropertyFormValues>
  cities: Cities[]
  setCities: React.Dispatch<React.SetStateAction<Cities[]>>
  images: PropertyImages[]
  setImages: React.Dispatch<React.SetStateAction<PropertyImages[]>>
  signers: ContractSignaturesSchema[]
  setSigners: React.Dispatch<React.SetStateAction<ContractSignaturesSchema[]>>
  documents: PropertyDocuments[]
  setDocuments: React.Dispatch<React.SetStateAction<PropertyDocuments[]>>
  typePublic: string
  setTypePublic: React.Dispatch<React.SetStateAction<string>>
  typePrivate: string
  setTypePrivate: React.Dispatch<React.SetStateAction<string>>

  docs: PropertyDocuments[] | null
  contratos: number | null
  loadingProperty: boolean

  hasPerimeterCoordinatesFile: boolean
  setHasPerimeterCoordinatesFile: React.Dispatch<React.SetStateAction<boolean>>

  perimeterCoordinates: PerimeterCoordinatesResponse | null
  setPerimeterCoordinates: React.Dispatch<
    React.SetStateAction<PerimeterCoordinatesResponse | null>
  >
  filePerimeterCoordinates: UploadPerimeterCoordinates
  setFilePerimeterCoordinates: React.Dispatch<
    React.SetStateAction<UploadPerimeterCoordinates>
  >
  isTheOwnerOfTheProperty: boolean
  setIsTheOwnerOfTheProperty: React.Dispatch<React.SetStateAction<boolean>>
}

export const PropertyFormContext = createContext<PropertyFormContextProps>(
  {} as PropertyFormContextProps,
)

export function PropertyFormProvider({ children }: { children: ReactNode }) {
  const { idProperty } = useParams()
  const navigate = useNavigate()

  const formProperty = useForm<PropertyFormValues>({
    resolver: zodResolver(propertyFormValidationSchema),
    defaultValues: {
      propriedade: {
        escala: '',
        carbono: false,
        taxa_preservacao: '',
        tipo_propriedade: {
          tipo: 'Privado',
        },

        numero_car: [{ value: '' }],
        numero_cib: [{ value: '' }],
        rppn: [{ date: '', darius: '', concierge: '', sectionAndPage: '' }],
        tipo_conservacao: '',
        tipo_coordenada_geografica: 'Polar',
        geografica_lat_orientacao: '',
        geografica_lng_orientacao: '',
        geografica_lat_decimal: '',
        geografica_lat_graus: '',
        geografica_lat_segundos: '',
        geografica_lng_decimal: '',
        geografica_lng_graus: '',
        geografica_lng_segundos: '',
        nome_bacia_hidrografica: '',
        geografica_lat_minutos: '',
        geografica_lng_minutos: '',
        dadosbasicos: {
          nome_propriedade: '',
          nome_gestor: '',
          sobre_propriedade: '',
          nome_proprietario: '',
          sobre_objetivo: '',
          nome_resumido: '',
        },
        endereco: {
          ano_criacao: '',
          area_preservada: '',
          bioma: '',
          cidade: '',
          estado: '',
          regiao: '',
          tipos_preservacao: [],
        },
      },
      dados_bancarios: {
        id_conta: '',
      },
      assinantes_contrato_propriedade: [],
    },
  })

  const maxUploadDocuments = Number(
    import.meta.env.VITE_MAX_PROPERTY_UPLOAD_LIMIT ??
      window.env.MAX_PROPERTY_UPLOAD_LIMIT,
  )

  const [cities, setCities] = useState<Cities[]>([])
  // Usando o useMemo no estado de cities para evitar re-renderizações
  const memoCities = useMemo(() => cities, [cities])

  const [images, setImages] = useState<PropertyImages[]>([])
  const [signers, setSigners] = useState<ContractSignaturesSchema[]>([])
  const [documents, setDocuments] = useState<PropertyDocuments[]>(
    Array(maxUploadDocuments).fill({
      nome_original: '',
      id_doc: '',
      url_obj: '',
      file: null,
      url: '',
      isFromBackend: false,
    }),
  )
  const [typePublic, setTypePublic] = useState<string>('Municipal')
  const [typePrivate, setTypePrivate] = useState<string>('RPPN')

  const [contratos, setContratos] = useState<number | null>(null)

  const [loadingProperty, setLoadingProperty] = useState<boolean>(false)
  const [hasPerimeterCoordinatesFile, setHasPerimeterCoordinatesFile] =
    useState<boolean>(false)

  const [docs, setDocs] = useState<PropertyDocuments[] | null>(null)

  const [perimeterCoordinates, setPerimeterCoordinates] =
    useState<PerimeterCoordinatesResponse | null>(null)

  const [filePerimeterCoordinates, setFilePerimeterCoordinates] =
    useState<UploadPerimeterCoordinates>({
      file: null,
      isFromFrontEnd: false,
    })

  const [isTheOwnerOfTheProperty, setIsTheOwnerOfTheProperty] =
    useState<boolean>(false)

  const getCity = useCallback(
    async (state: string, selectedCity: string) => {
      await api
        .get(`/cities/${state}`)
        .then((response) => {
          setCities(response.data)
          formProperty.setValue('propriedade.endereco.cidade', selectedCity)
        })
        .catch((error) => {
          toast.error(error.response.data.message)
        })
    },
    [formProperty],
  )

  const [openModal, setOpenModal] = useState(false)

  // Chama o endpoint do back-end e preenche os inputs com o que vem na response
  useEffect(() => {
    async function getByIdProperty() {
      setLoadingProperty(true)
      await api
        .get<PropertyFormResponse>(`/property/form/${idProperty}`)
        .then((response) => {
          setSigners(response.data.assinantes_contrato_propriedade)
          setContratos(response.data.contratos.ack_version)
          setHasPerimeterCoordinatesFile(
            response.data.arquivo_perimetro_importado,
          )
          setIsTheOwnerOfTheProperty(response.data.proprietario_ou_supervisor)
          setPerimeterCoordinates(response.data.coordenadas_perimetro)
          setImages(response.data.imagens)
          setDocs(response.data.documentos)
          if (response.data.tipo_propriedade.tipo === 'Publica') {
            setTypePublic(response.data.tipo_propriedade.descricao)
          }
          if (response.data.tipo_propriedade.tipo === 'Privado') {
            setTypePrivate(response.data.tipo_propriedade.descricao)
          }
          formProperty.reset({
            dados_bancarios: {
              id_conta: response.data.dados_bancarios.id_conta,
            },
            assinantes_contrato_propriedade:
              response.data.assinantes_contrato_propriedade ?? [],
            propriedade: {
              escala: response.data.escala,
              status: response.data.status,
              carbono: response.data.carbono,
              emissoes_internas:
                response.data.emissoes_internas?.toFixed(2) ?? undefined,
              numero_car: response.data.numero_car.map((value) => ({ value })),
              numero_cib: response.data.numero_cib?.map((value) => ({
                value,
              })) ?? [{ value: '' }],
              rppn: response.data.rppn,
              tipo_conservacao: response.data.tipo_conservacao,
              dadosbasicos: response.data.dadosbasicos,

              tipo_propriedade: {
                tipo: response.data.tipo_propriedade.tipo,
              },
              endereco: response.data.endereco,
              geografica_lat_graus:
                response.data.coordenadas.latitude_polar?.graus,

              geografica_lat_decimal:
                response.data.coordenadas.latitude_decimal,

              geografica_lng_decimal:
                response.data.coordenadas.longitude_decimal,

              geografica_lat_minutos:
                response.data.coordenadas.latitude_polar?.minutos,

              geografica_lat_segundos:
                response.data.coordenadas.latitude_polar?.segundos,
              geografica_lat_orientacao:
                response.data.coordenadas.latitude_polar?.orientacao,

              geografica_lng_graus:
                response.data.coordenadas.longitude_polar?.graus,

              geografica_lng_minutos:
                response.data.coordenadas.longitude_polar?.minutos,

              geografica_lng_segundos:
                response.data.coordenadas.longitude_polar?.segundos,
              geografica_lng_orientacao:
                response.data.coordenadas.longitude_polar?.orientacao,

              tipo_coordenada_geografica:
                response.data.coordenadas.tipo_coordenada,
              nome_bacia_hidrografica:
                response.data.bacia_hidrografica.nome_bacia,
              taxa_preservacao:
                response.data.taxa_preservacao === 0
                  ? ''
                  : formatNumberToCurrency(response.data.taxa_preservacao),
            },
          })
          getCity(response.data.endereco.estado, response.data.endereco.cidade)
        })
        .catch((error) => {
          if (error.response.status === 403) {
            toast.error(
              'Somente o proprietário desta propriedade pode editá-la.',
            )
            navigate('/dashboard')
            return
          }
          toast.error(error.response.data.message)
        })
        .finally(() => {
          setLoadingProperty(false)
        })
    }
    if (idProperty) {
      getByIdProperty()
    }
  }, [formProperty, getCity, idProperty, navigate])

  const ignoredPaths = [
    '/add-property/perimeter-coordinates',
    '/edit-property/perimeter-coordinates',
    '/add-property',
    '/edit-property',
  ]

  const shouldIgnorePath = (pathname: string) => {
    return ignoredPaths.some((path) => pathname.startsWith(path))
  }

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (shouldIgnorePath(nextLocation.pathname)) {
      return false
    }

    const hasUnsavedImages = images?.some((img) => img.url)
    const hasBackendDocuments = documents?.some(
      (doc) => !doc.isFromBackend && doc.file,
    )

    return (
      (formProperty.formState.isDirty ||
        hasUnsavedImages ||
        hasBackendDocuments) &&
      !formProperty.formState.isSubmitSuccessful &&
      currentLocation.pathname !== nextLocation.pathname
    )
  })

  // Responsável por exibir o modal ou deixar o usuário sair da tela
  useEffect(() => {
    const hasUnsavedImages = images?.some((img) => img.url)
    const hasBackendDocuments = documents?.some(
      (doc) => !doc.isFromBackend && doc.file,
    )

    if (
      formProperty.formState.isSubmitSuccessful &&
      blocker.state === 'blocked'
    ) {
      blocker.proceed()
    }

    if (
      blocker.state === 'blocked' &&
      (formProperty.formState.isDirty ||
        hasUnsavedImages ||
        hasBackendDocuments) &&
      !formProperty.formState.isSubmitSuccessful
    ) {
      setOpenModal(true)
    }
  }, [
    blocker,
    documents,
    formProperty.formState.isDirty,
    formProperty.formState.isSubmitSuccessful,
    images,
  ])

  function handleConfirm() {
    if (blocker.state === 'blocked') {
      setOpenModal(false)
      blocker.proceed()
    }
  }

  function handleCancel() {
    if (blocker.state === 'blocked') {
      setOpenModal(false)
      blocker.reset()
    }
  }

  return (
    <PropertyFormContext.Provider
      value={{
        hasPerimeterCoordinatesFile,
        setHasPerimeterCoordinatesFile,
        formProperty,
        cities: memoCities,
        setCities,
        images,
        setImages,
        documents,
        setDocuments,
        typePublic,
        setTypePublic,
        typePrivate,
        setTypePrivate,
        contratos,
        loadingProperty,
        docs,
        perimeterCoordinates,
        setPerimeterCoordinates,
        filePerimeterCoordinates,
        setFilePerimeterCoordinates,
        signers,
        setSigners,
        isTheOwnerOfTheProperty,
        setIsTheOwnerOfTheProperty,
      }}
    >
      <Dialog
        open={openModal}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            handleCancel()
          }
        }}
      >
        {children}

        <DialogContent className="h-auto max-w-120">
          <DialogHeader>
            <DialogTitle>
              Tem certeza que deseja sair sem salvar as alterações?
            </DialogTitle>
          </DialogHeader>

          <DialogFooter>
            <Button className="w-full" onClick={handleConfirm}>
              Sair
            </Button>

            <Button
              variant="tertiary"
              className="w-full"
              onClick={handleCancel}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PropertyFormContext.Provider>
  )
}
